import { ComponentPropsWithoutRef, FC, HTMLAttributes, ReactNode, useCallback } from 'react'
import styled from 'styled-components'

import { Carat, Trash, TypeStyle, Warning, fromMd, untilMd } from '@syconium/little-miss-figgy'

import { ProductCatalogEmbroideryTypes } from '../../../../__generated__/graphql/catalog/graphql'
import { useEmbroidery } from '../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { EmbroideryFormKind } from '../../../../containers/EmbroideryContainer/types'
/**
 * Embroidery option selector list wrapper.
 */
const List: FC<HTMLAttributes<HTMLUListElement>> = ({ children, ...rest }) => {
  return <ul {...rest}>{children}</ul>
}

/**
 * Embroidery option selector list item.
 *
 *
 * @param props
 * @param props.index - The index of the given option.
 * @param props.handleClick - Callback function for when the option is clicked.
 */
type EmbroideryOptionProps = {
  index: number
  disabled?: boolean
  selected: boolean
  kind: EmbroideryFormKind
  handleClick: (kind: EmbroideryFormKind) => void
  label?: string
  error?: ReactNode
  errorMessageId?: string
} & HTMLAttributes<HTMLLIElement>

const ListOption: FC<EmbroideryOptionProps> = ({
  children,
  index,
  disabled = false,
  kind,
  label,
  handleClick,
  selected,
  error,
  errorMessageId,
}) => {
  const handleKeyDown: React.KeyboardEventHandler = e => {
    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
      handleClick(kind)
    }
  }

  return (
    <li key={index}>
      <ListButton
        aria-label={`${label}`}
        aria-describedby={errorMessageId}
        aria-invalid={Boolean(error)}
        aria-disabled={disabled}
        onClick={() => !disabled && handleClick(kind)}
        onKeyDown={handleKeyDown}
        $selected={selected}
        $disabled={disabled}
        tabIndex={selected ? undefined : 0}
        role={selected ? undefined : 'button'}
      >
        <ListItemContent>{children}</ListItemContent>
        {error && <ListItemError>{error}</ListItemError>}
      </ListButton>
    </li>
  )
}

/**
 * The right-most content on the embroidery option.
 */
const RightContent: FC<{ $disabled?: boolean; children: ReactNode }> = ({
  children,
  $disabled,
}) => {
  return <RightContentWrap $disabled={$disabled}>{children}</RightContentWrap>
}

export const Icon: FC<{ selected: boolean; embroideryType: ProductCatalogEmbroideryTypes }> = ({
  selected,
  embroideryType,
}) => {
  const { clearEmbroiderySelectionsByType } = useEmbroidery()

  const handleClear = useCallback(() => {
    clearEmbroiderySelectionsByType(embroideryType)
  }, [clearEmbroiderySelectionsByType, embroideryType])

  return (
    <IconWrap>
      {selected ? (
        <Trash
          role='button'
          tabIndex={0}
          aria-label='Remove embroidery option'
          onClick={handleClear}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              handleClear()
            }
          }}
        />
      ) : (
        <Carat />
      )}
    </IconWrap>
  )
}

/**
 * An error message that should be displayed alongside an invalid embroidery option.
 */
const Error: FC<
  { message?: string; id: string } & Omit<ComponentPropsWithoutRef<typeof ErrorMessage>, 'children'>
> = ({ id, message, ...rest }) => {
  return (
    <ErrorMessage id={id} {...rest}>
      <ErrorIcon width='16px' height='16px' /> {message}
    </ErrorMessage>
  )
}

export const EmbroideryOptionComponents = {
  Error,
  List,
  RightContent,
  Icon,
  ListOption,
}

const ErrorMessage = styled(TypeStyle.BodyNano).attrs({ asTag: 'span' })`
  font-weight: 600;
  color: ${o => o.theme.color.stroke.critical.text.on.background};
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
`

const ErrorIcon = styled(Warning).attrs(o => ({ fill: o.theme.color.fill.critical.subtle }))``

const ListButton = styled.article<{ $selected: boolean; $disabled: boolean }>`
  ${TypeStyle.css.headlineMicro}
  width: 100%;
  padding: ${o => o.theme.spacing(5)} 0;
  border-bottom: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};

  ${fromMd} {
    padding: ${o => o.theme.spacing(5)} 0;
  }

  &:hover {
    cursor: ${o => (o.$selected ? 'default' : o.$disabled ? 'not-allowed' : 'pointer')};
  }

  li:first-child & {
    border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  }
`

const ListItemContent = styled.div`
  display: flex;
  justify-content: space-between;
`
const ListItemError = styled.div`
  margin-top: ${o => o.theme.spacing(2)};
`

const IconWrap = styled.div`
  padding-left: ${o => o.theme.spacing(2)};
  cursor: pointer;
`
const RightContentWrap = styled.div<{ $disabled?: boolean }>`
  ${TypeStyle.css.headlineMicro}
  color: ${o =>
    o.$disabled
      ? o.theme.color.stroke.disabled.text.on.background
      : o.theme.color.text.primary.on.background}
  font-weight: ${o => o.theme.typography.font.weight[700]};
  display: flex;
  align-items: center;

  ${untilMd} {
    margin-right: ${o => o.theme.spacing(1)};
  }
`
